// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {attachTo, VNode} from 'snabbdom';

import {JsxFactory, PojoAttrsJsxProps, StrictJsxProps} from './jsx';

/** *
 * portalify: returns a function that renders the provided element at a higher level in the DOM.
 *
 * Uses snabbdom's `attachTo` utility to mount an element onto a different DOM
 * path from where the returned function is rendered, it in-place renderers a
 * placeholder element while patching the dropdown element mounted in a different place.
 *
 * see: https://github.com/snabbdom/snabbdom/blob/master/src/helpers/attachto.ts
 * @todo [pablo] this portal implementation is experimental
 * and needs a lot of refinement before it can be used for any element.
 *
 * DO NOT USE IF:
 * - you need to provide slotted children that require styles from the caller component
 * - you need to bubble custom events to a component that is not the caller component
 */
export function portalify<PropsT extends PojoAttrsJsxProps>(jsxFactory: JsxFactory<PropsT>) {
  const ElementToPortal = jsxFactory;

  return function (props: StrictJsxProps<PropsT>, children: VNode[]) {
    const el = <ElementToPortal {...props}>{children}</ElementToPortal>;

    // TODO pjboudrx: remove the class selector fall-back, a work-around for iron-unit-tests.
    // Even though #width_wrapper is in the HTML, we cannot find it by id, when running in unit tests. But the class selector works.
    const vnode = attachTo(
      document.querySelector(`#width_wrapper`) ??
        document.querySelector(`.width_wrapper`) ??
        document.querySelector(`mp-browser-context-root`) ??
        document.body,
      el,
    );

    const defaultDestroyHook = vnode.data?.hook?.destroy;
    vnode.data.hook.destroy = (node: VNode) => {
      defaultDestroyHook?.(node);

      /**
       * Bug/gap of attachTo:
       *
       * If the portaled element is a child (or descendent) of a vnode that is being removed,
       * snabbdom will assume it has been removed properly. This will leave the portaled element in the DOM.
       *
       * This check will run after hooks are completed and clean up the DOM in that case.
       */
      requestAnimationFrame(() => {
        if (node.elm?.parentNode?.contains(node.elm)) {
          node.elm.parentNode.removeChild(node.elm);
        }
      });
    };

    return vnode;
  };
}
